import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { 
  XMarkIcon, 
  PhotoIcon, 
  TrashIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon,
  ArrowUpTrayIcon,
  StarIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { Project } from '../../types/project';
import { projectService } from '../../services/projectService';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  onSave: (project: Omit<Project, 'id'>) => void;
}

interface FormErrors {
  title?: string;
  location?: string;
  squareFeet?: string;
  completionDate?: string;
  description?: string;
}

const ProjectModal = ({ isOpen, onClose, project, onSave }: ProjectModalProps) => {
  const [formData, setFormData] = useState<Omit<Project, 'id'>>({
    title: '',
    status: 'Planning',
    location: '',
    squareFeet: '',
    completionDate: '',
    featured: false,
    description: '',
    images: [],
  });
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<boolean>(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isDragging, setIsDragging] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  useEffect(() => {
    if (project) {
      const { id, ...projectData } = project;
      setFormData(projectData);
      setPreviewImages(projectData.images || []);
    } else {
      setFormData({
        title: '',
        status: 'Planning',
        location: '',
        squareFeet: '',
        completionDate: '',
        featured: false,
        description: '',
        images: [],
      });
      setPreviewImages([]);
      setUploadedImages([]);
    }
    setHasUnsavedChanges(false);
    setErrors({});
  }, [project, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Project title is required';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.squareFeet.trim()) {
      newErrors.squareFeet = 'Square footage is required';
    } else if (isNaN(Number(formData.squareFeet))) {
      newErrors.squareFeet = 'Square footage must be a number';
    }
    
    if (!formData.completionDate) {
      newErrors.completionDate = 'Completion date is required';
    }
    
    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setUploadProgress(true);
    setUploadPercentage(0);
    
    try {
      // If there are new images to upload
      if (uploadedImages.length > 0) {
        const projectId = project?.id || 'temp-id';
        
        let completedUploads = 0;
        const totalUploads = uploadedImages.length;
        
        const uploadPromises = uploadedImages.map(file => 
          projectService.uploadProjectImage(file, projectId).then(url => {
            completedUploads++;
            setUploadPercentage(Math.round((completedUploads / totalUploads) * 100));
            return url;
          })
        );
        
        const uploadedUrls = await Promise.all(uploadPromises);
        
        // Combine existing images with new ones
        const allImages = [...(formData.images || []), ...uploadedUrls];
        
        // Submit the form with all images
        onSave({
          ...formData,
          images: allImages
        });
      } else {
        // No new images to upload
        onSave(formData);
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setUploadProgress(false);
      onClose();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    
    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
    
    setHasUnsavedChanges(true);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addImages(Array.from(e.target.files));
    }
  };
  
  const addImages = (files: File[]) => {
    // Filter for image files and size < 10MB
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024
    );
    
    if (validFiles.length > 0) {
      setUploadedImages(prev => [...prev, ...validFiles]);
      
      // Create preview URLs
      const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
      setPreviewImages(prev => [...prev, ...newPreviewUrls]);
      setHasUnsavedChanges(true);
    }
    
    if (validFiles.length < files.length) {
      alert('Some files were skipped. Only images under 10MB are allowed.');
    }
  };

  const handleRemoveImage = (index: number, isExisting: boolean) => {
    if (isExisting) {
      // Remove existing image
      const updatedImages = [...(formData.images || [])];
      updatedImages.splice(index, 1);
      setFormData(prev => ({
        ...prev,
        images: updatedImages
      }));
      
      // Update preview images
      const updatedPreviews = [...previewImages];
      updatedPreviews.splice(index, 1);
      setPreviewImages(updatedPreviews);
    } else {
      // Remove newly uploaded image
      const uploadedIndex = index - (formData.images?.length || 0);
      if (uploadedIndex >= 0) {
        const updatedUploads = [...uploadedImages];
        updatedUploads.splice(uploadedIndex, 1);
        setUploadedImages(updatedUploads);
        
        // Update preview images
        const updatedPreviews = [...previewImages];
        updatedPreviews.splice(index, 1);
        setPreviewImages(updatedPreviews);
      }
    }
    
    setHasUnsavedChanges(true);
  };

  // New function to set an image as the cover image (moves it to the first position)
  const setAsCoverImage = (index: number) => {
    // If it's already the cover image (index 0), do nothing
    if (index === 0) return;

    // For existing images in formData
    if (index < (formData.images?.length || 0)) {
      const updatedImages = [...(formData.images || [])];
      const imageToMove = updatedImages[index];
      
      // Remove the image from its current position
      updatedImages.splice(index, 1);
      // Add it to the beginning
      updatedImages.unshift(imageToMove);
      
      setFormData(prev => ({
        ...prev,
        images: updatedImages
      }));
    }
    
    // Update preview images to match (this affects both existing and newly uploaded images)
    const updatedPreviews = [...previewImages];
    const previewToMove = updatedPreviews[index];
    
    // Remove the preview from its current position
    updatedPreviews.splice(index, 1);
    // Add it to the beginning
    updatedPreviews.unshift(previewToMove);
    
    setPreviewImages(updatedPreviews);
    
    // If the moved image was a newly uploaded image, we need to update uploadedImages as well
    const existingImagesCount = formData.images?.length || 0;
    if (index >= existingImagesCount) {
      const uploadedIndex = index - existingImagesCount;
      const updatedUploads = [...uploadedImages];
      const uploadToMove = updatedUploads[uploadedIndex];
      
      // Remove the upload from its current position
      updatedUploads.splice(uploadedIndex, 1);
      // Add it to the beginning of uploads - this is tricky because we need to handle mixing of existing and new
      // For simplicity, we'll just reorder the uploads array and will handle merge during save
      updatedUploads.unshift(uploadToMove);
      
      setUploadedImages(updatedUploads);
    }
    
    setHasUnsavedChanges(true);
  };
  
  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addImages(Array.from(e.dataTransfer.files));
    }
  }, []);
  
  const handleCloseWithCheck = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleCloseWithCheck} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        {showUnsavedWarning ? (
          <Dialog.Panel className="mx-auto max-w-md w-full rounded-xl bg-white p-6">
            <div className="mb-5">
              <div className="flex justify-center items-center mb-4 text-amber-600">
                <ExclamationCircleIcon className="h-12 w-12" />
              </div>
              <Dialog.Title className="text-lg font-medium text-primary-900 text-center">
                Unsaved Changes
              </Dialog.Title>
              <p className="mt-2 text-sm text-primary-500 text-center">
                You have unsaved changes. Are you sure you want to close without saving?
              </p>
            </div>
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                onClick={() => setShowUnsavedWarning(false)}
                className="rounded-md border border-primary-300 bg-white px-4 py-2 text-sm font-medium text-primary-700 shadow-sm hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
              >
                Continue Editing
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUnsavedWarning(false);
                  onClose();
                }}
                className="rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Discard Changes
              </button>
            </div>
          </Dialog.Panel>
        ) : (
          <Dialog.Panel className="mx-auto max-w-2xl w-full rounded-xl bg-white p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 border-b border-primary-200 pb-4">
              <Dialog.Title className="text-xl font-semibold text-primary-900">
                {project ? 'Edit Project' : 'Add New Project'}
              </Dialog.Title>
              <button
                type="button"
                className="text-primary-400 hover:text-primary-500"
                onClick={handleCloseWithCheck}
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Project Details Section */}
              <div className="bg-primary-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-primary-900 mb-4">Project Details</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-primary-700">
                      Project Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      id="title"
                      value={formData.title}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 ${
                        errors.title
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-primary-300 focus:border-accent-500 focus:ring-accent-500'
                      }`}
                      required
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-primary-700">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="status"
                        id="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-primary-300 shadow-sm focus:border-accent-500 focus:ring-accent-500 sm:text-sm px-3 py-2"
                      >
                        <option value="Planning">Planning</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-primary-700">
                        Location <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        id="location"
                        value={formData.location}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 ${
                          errors.location
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-primary-300 focus:border-accent-500 focus:ring-accent-500'
                        }`}
                        required
                      />
                      {errors.location && (
                        <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="squareFeet" className="block text-sm font-medium text-primary-700">
                        Square Feet <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="squareFeet"
                        id="squareFeet"
                        value={formData.squareFeet}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 ${
                          errors.squareFeet
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-primary-300 focus:border-accent-500 focus:ring-accent-500'
                        }`}
                        required
                        placeholder="e.g. 2500"
                      />
                      {errors.squareFeet && (
                        <p className="mt-1 text-sm text-red-600">{errors.squareFeet}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="completionDate" className="block text-sm font-medium text-primary-700">
                        Completion Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="completionDate"
                        id="completionDate"
                        value={formData.completionDate}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 ${
                          errors.completionDate
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-primary-300 focus:border-accent-500 focus:ring-accent-500'
                        }`}
                        required
                      />
                      {errors.completionDate && (
                        <p className="mt-1 text-sm text-red-600">{errors.completionDate}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-primary-700">
                      Description
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows={4}
                      value={formData.description}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 ${
                        errors.description
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-primary-300 focus:border-accent-500 focus:ring-accent-500'
                      }`}
                      placeholder="Describe the project in detail..."
                    />
                    {errors.description ? (
                      <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                    ) : (
                      <p className="mt-1 text-xs text-primary-500">
                        {formData.description.length}/1000 characters
                      </p>
                    )}
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="featured"
                      id="featured"
                      checked={formData.featured}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-primary-300 text-accent-600 focus:ring-accent-500"
                    />
                    <label htmlFor="featured" className="ml-2 block text-sm text-primary-700">
                      Feature this project on the homepage
                    </label>
                  </div>
                </div>
              </div>

              {/* Project Images Section */}
              <div className="bg-primary-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-primary-900 mb-2">Project Images</h3>
                <p className="text-sm text-primary-500 mb-4">
                  Add high-quality images to showcase your project. The first image will be used as the cover image.
                </p>
                
                {/* Image preview grid */}
                {previewImages.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2 mb-4">
                    {previewImages.map((img, index) => (
                      <div key={index} className="relative group aspect-video">
                        <img 
                          src={img} 
                          alt={`Project image ${index + 1}`} 
                          className="h-full w-full object-cover rounded-md"
                        />
                        {index === 0 && (
                          <div className="absolute top-1 left-1 bg-accent-600 text-white text-xs py-1 px-2 rounded-md">
                            Cover
                          </div>
                        )}
                        <div className="absolute inset-0 flex flex-col justify-between p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex justify-between">
                            {index !== 0 && (
                              <button
                                type="button"
                                onClick={() => setAsCoverImage(index)}
                                className="bg-yellow-500 text-white p-1 rounded-full hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
                                title="Set as cover image"
                              >
                                <StarIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index, index < (formData.images?.length || 0))}
                              className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors ml-auto"
                              title="Remove image"
                            >
                              <TrashIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {previewImages.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-md text-sm text-blue-700 flex items-start">
                    <InformationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                    <p>
                      <strong>Change Cover Image:</strong> Hover over any image and click the star icon to set it as the cover image. The cover image is displayed first and used on the project cards.
                    </p>
                  </div>
                )}
                
                {/* Image upload */}
                <div 
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                    isDragging 
                      ? 'border-accent-500 bg-accent-50' 
                      : 'border-primary-300 hover:border-accent-400'
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    {isDragging ? (
                      <>
                        <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-accent-500" aria-hidden="true" />
                        <p className="text-accent-600 font-medium">Drop images here</p>
                      </>
                    ) : (
                      <>
                        <PhotoIcon className="mx-auto h-12 w-12 text-primary-400" aria-hidden="true" />
                        <div className="flex text-sm text-primary-600 justify-center">
                          <label
                            htmlFor="image-upload"
                            className="relative cursor-pointer rounded-md font-medium text-accent-600 hover:text-accent-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-accent-500"
                          >
                            <span>Upload images</span>
                            <input
                              id="image-upload"
                              name="image-upload"
                              type="file"
                              multiple
                              accept="image/*"
                              className="sr-only"
                              onChange={handleImageUpload}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-primary-500">PNG, JPG, GIF up to 10MB</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-primary-200">
                {uploadProgress && (
                  <div className="flex items-center mr-auto text-sm text-primary-600">
                    <div className="w-20 bg-primary-200 rounded-full h-2.5 mr-2">
                      <div 
                        className="bg-accent-600 h-2.5 rounded-full" 
                        style={{ width: `${uploadPercentage}%` }}
                      ></div>
                    </div>
                    <span>Uploading {uploadPercentage}%</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleCloseWithCheck}
                  className="rounded-md border border-primary-300 bg-white px-4 py-2 text-sm font-medium text-primary-700 shadow-sm hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
                  disabled={uploadProgress}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md border border-transparent bg-accent-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
                  disabled={uploadProgress}
                >
                  {uploadProgress ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    project ? 'Save Changes' : 'Add Project'
                  )}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        )}
      </div>
    </Dialog>
  );
};

export default ProjectModal; 